import React, { useState, useEffect } from 'react';
import { ArrowLeft, MapPin, Home, Utensils, Calendar, Lightbulb, Copy, Check, Sparkles, Share2, Loader2, Map, Briefcase, ChevronRight, Crown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';
import { generateItinerary } from '../services/api';
import { useUser } from '../context/UserContext';
import OfflineMapsPage from './OfflineMaps';
import SmartPackingPage from './SmartPacking';

const tabVariants = {
  hidden: { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.35, type: 'spring' } },
  exit: { opacity: 0, y: -12, transition: { duration: 0.2 } }
};

const buttonVariants = {
  tap: { scale: 0.96 },
  hover: { scale: 1.02, y: -2 }
};

const itemVariants = {
  hidden: { opacity: 0, x: -12 },
  visible: { opacity: 1, x: 0, transition: { type: 'spring', stiffness: 260, damping: 20 } }
};

// Skeleton for loading states - Clay Style
function ContentSkeleton() {
  return (
    <div className="space-y-4">
      {[1, 2, 3].map((i) => (
        <div key={i} className="p-4 rounded-2xl" style={{ backgroundColor: 'var(--clay-white)' }}>
          <div className="swoopy-skeleton h-5 w-36 rounded-lg mb-2" />
          <div className="swoopy-skeleton h-4 w-52 rounded-lg mb-3" />
          <div className="swoopy-skeleton h-4 w-full rounded-lg" />
        </div>
      ))}
    </div>
  );
}

export default function TripDetailsPage({ trip, userProfile, onBack }) {
  const [activeTab, setActiveTab] = useState('budget');
  const [copiedText, setCopiedText] = useState('');
  const [aiItinerary, setAiItinerary] = useState(null);
  const [itineraryLoading, setItineraryLoading] = useState(false);
  const [itineraryError, setItineraryError] = useState(null);
  const [currentView, setCurrentView] = useState('details'); // 'details', 'offline-maps', 'smart-packing'
  const { userStatus } = useUser();

  // Format currency using the selected currency from context
  // trip.currency is typically 'USD' from backend, but we display in selected currency
  const formatCurrency = (amount, tripCurrency = 'USD') => {
    // If amount is in USD (from backend), convert to IDR first then to selected currency
    // Backend sends costs in USD, we convert to IDR for display
    const USD_TO_IDR = 15800; // Exchange rate
    const amountInIDR = tripCurrency === 'USD' ? amount * USD_TO_IDR : amount;
    
    // Format in IDR with millions notation
    if (amountInIDR >= 1000000) {
      const millions = (amountInIDR / 1000000).toFixed(1);
      return `Rp${millions.replace('.0', '')}jt`;
    }
    return `Rp${Math.round(amountInIDR).toLocaleString('id-ID')}`;
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    setCopiedText(label);
    toast.success('Copied! 📋');
    setTimeout(() => setCopiedText(''), 2000);
  };

  const shareTrip = () => {
    const shareText = `✈️ ${trip.title}\n📍 ${trip.destination}, ${trip.country}\n💰 From ${formatCurrency(trip.totalBudgetMin, trip.currency)}\n🎯 ${trip.swoopyMatch}% match\n\nPlanned with Swoopy 🐦`;
    navigator.clipboard.writeText(shareText);
    toast.success('Trip copied! Share with your travel buddy 🎉');
  };

  const tabs = [
    { id: 'budget', label: 'Budget', icon: '💰' },
    { id: 'stay', label: 'Stay', icon: '🏨' },
    { id: 'eat', label: 'Eat', icon: '🍜' },
    { id: 'plan', label: 'Plan', icon: '📅' },
  ];

  useEffect(() => {
    if (activeTab === 'plan' && !aiItinerary && !itineraryLoading) {
      fetchAiItinerary();
    }
  }, [activeTab]);

  const fetchAiItinerary = async () => {
    setItineraryLoading(true);
    setItineraryError(null);
    
    try {
      const durationMatch = trip.duration?.match(/\d+/);
      const days = durationMatch ? parseInt(durationMatch[0]) : 4;
      const budget = trip.totalBudgetMin || trip.cost || 500;
      const vibe = trip.vibe || 'Balanced';
      
      const result = await generateItinerary(trip.destination, days, budget, vibe);
      setAiItinerary(result);
    } catch (error) {
      console.error('Failed to generate itinerary:', error);
      setItineraryError('Could not generate AI itinerary. Using default plan.');
    } finally {
      setItineraryLoading(false);
    }
  };

  // Sub-view rendering
  if (currentView === 'offline-maps') {
    return <OfflineMapsPage trip={trip} onBack={() => setCurrentView('details')} />;
  }

  if (currentView === 'smart-packing') {
    return <SmartPackingPage trip={trip} onBack={() => setCurrentView('details')} />;
  }

  return (
    <div className="min-h-screen pb-8" style={{ backgroundColor: 'var(--clay-white)' }}>
      {/* Header - Clay Style */}
      <header 
        className="sticky top-0 z-50 px-4 py-3"
        style={{ 
          backgroundColor: 'rgba(248, 248, 248, 0.92)',
          backdropFilter: 'blur(12px)',
          boxShadow: '0 2px 10px rgba(0,0,0,0.04)'
        }}
      >
        <div className="max-w-lg mx-auto flex items-center justify-between">
          <motion.button 
            onClick={onBack} 
            className="flex items-center gap-2 p-2 -ml-2 rounded-xl"
            style={{ color: 'var(--text-secondary)' }}
            whileTap={{ scale: 0.95 }}
            whileHover={{ backgroundColor: 'var(--swoopy-pink-soft)' }}
          >
            <ArrowLeft size={20} strokeWidth={2.5} />
            <span className="text-sm font-bold">Back</span>
          </motion.button>
          <motion.button 
            onClick={shareTrip}
            className="flex items-center gap-2 h-10 px-5 rounded-full text-sm font-bold relative overflow-hidden"
            style={{ 
              backgroundColor: 'var(--card-bg)', 
              color: 'var(--swoopy-pink)',
              boxShadow: 'var(--shadow-clay-sm)'
            }}
            variants={buttonVariants}
            whileTap="tap"
            whileHover="hover"
          >
            <div 
              className="absolute top-0 left-0 right-0 h-1/2 pointer-events-none"
              style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.6) 0%, rgba(255,255,255,0) 100%)' }}
            />
            <Share2 size={16} className="relative z-10" />
            <span className="relative z-10">Share</span>
          </motion.button>
        </div>
      </header>

      <div className="max-w-lg mx-auto px-4">
        {/* Trip Header */}
        <motion.div 
          className="mb-6 pt-3"
          initial={{ opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, type: 'spring' }}
        >
          <motion.div 
            className="swoopy-match mb-4 inline-flex"
            initial={{ scale: 0.9 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', stiffness: 300, delay: 0.1 }}
          >
            <Sparkles size={15} />
            {trip.swoopyMatch}% Match
          </motion.div>
          <h1 style={{ 
            fontSize: '28px', 
            fontWeight: '800', 
            color: 'var(--text-primary)', 
            marginBottom: '8px', 
            lineHeight: '1.2',
            fontFamily: "'Nunito', sans-serif"
          }}>
            {trip.title}
          </h1>
          <div className="flex items-center gap-2 text-sm mb-4" style={{ color: 'var(--text-muted)' }}>
            <MapPin size={16} />
            <span className="font-semibold">{trip.destination}, {trip.country}</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {trip.tags.map((tag, index) => (
              <motion.span 
                key={index} 
                className="px-4 py-2 rounded-full text-xs font-bold relative overflow-hidden"
                style={{ 
                  backgroundColor: 'var(--swoopy-pink-soft)', 
                  color: 'var(--swoopy-pink)',
                  boxShadow: 'var(--shadow-clay-xs)'
                }}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.1 + index * 0.05, type: 'spring' }}
              >
                <div 
                  className="absolute top-0 left-0 right-0 h-1/2 pointer-events-none"
                  style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.4) 0%, rgba(255,255,255,0) 100%)' }}
                />
                <span className="relative z-10">{tag}</span>
              </motion.span>
            ))}
          </div>
        </motion.div>

        {/* Clay Tabs */}
        <motion.div 
          className="flex gap-1.5 p-2 rounded-2xl mb-6 relative overflow-hidden"
          style={{ 
            backgroundColor: 'var(--card-bg)', 
            boxShadow: 'var(--shadow-clay-md)'
          }}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, type: 'spring' }}
        >
          <div 
            className="absolute top-0 left-0 right-0 h-1/2 pointer-events-none"
            style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.5) 0%, rgba(255,255,255,0) 100%)' }}
          />
          {tabs.map((tab) => (
            <motion.button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className="flex-1 py-3.5 px-2 rounded-xl text-sm font-bold transition-all flex items-center justify-center gap-1.5 relative overflow-hidden"
              style={{
                background: activeTab === tab.id 
                  ? 'linear-gradient(145deg, var(--swoopy-pink-light), var(--swoopy-pink), var(--swoopy-pink-dark))' 
                  : 'transparent',
                color: activeTab === tab.id ? 'white' : 'var(--text-muted)',
                boxShadow: activeTab === tab.id ? 'var(--shadow-clay-btn)' : 'none'
              }}
              whileTap={{ scale: 0.97 }}
            >
              {activeTab === tab.id && (
                <div 
                  className="absolute top-0 left-0 right-0 h-1/2 pointer-events-none"
                  style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.25) 0%, rgba(255,255,255,0) 100%)' }}
                />
              )}
              <span className="relative z-10">{tab.icon}</span>
              <span className="hidden sm:inline relative z-10">{tab.label}</span>
            </motion.button>
          ))}
        </motion.div>

        {/* Tab Content */}
        <AnimatePresence mode="wait">
          {/* Budget Tab */}
          {activeTab === 'budget' && (
            <motion.div 
              key="budget"
              className="swoopy-card p-5"
              variants={tabVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
            >
              <h2 style={{ 
                fontSize: '19px', 
                fontWeight: '800', 
                color: 'var(--text-primary)', 
                marginBottom: '20px',
                fontFamily: "'Nunito', sans-serif"
              }}>
                Budget Breakdown 💰
              </h2>
              
              <div className="space-y-3">
                {[
                  { label: 'Accommodation', sub: `${formatCurrency(trip.details.budgetBreakdown.accommodation.perNight, trip.currency)}/night`, total: trip.details.budgetBreakdown.accommodation.total },
                  { label: 'Food & Dining', sub: `${formatCurrency(trip.details.budgetBreakdown.food.perDay, trip.currency)}/day`, total: trip.details.budgetBreakdown.food.total },
                  { label: 'Getting Around', sub: 'Local transport', total: trip.details.budgetBreakdown.transport.total },
                  { label: 'Activities', sub: 'Tours & tickets', total: trip.details.budgetBreakdown.activities.total },
                ].map((item, i) => (
                  <motion.div 
                    key={i} 
                    className="flex items-center justify-between p-4 rounded-2xl relative overflow-hidden"
                    style={{ 
                      backgroundColor: 'var(--clay-white)',
                      boxShadow: 'var(--shadow-clay-xs)'
                    }}
                    variants={itemVariants}
                    initial="hidden"
                    animate="visible"
                    transition={{ delay: i * 0.05 }}
                  >
                    <div 
                      className="absolute top-0 left-0 right-0 h-1/2 pointer-events-none"
                      style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.6) 0%, rgba(255,255,255,0) 100%)' }}
                    />
                    <div className="relative z-10">
                      <p className="font-bold" style={{ color: 'var(--text-primary)' }}>{item.label}</p>
                      <p className="text-xs font-semibold" style={{ color: 'var(--text-muted)' }}>{item.sub}</p>
                    </div>
                    <p className="font-extrabold text-lg relative z-10" style={{ color: 'var(--text-primary)' }}>
                      {formatCurrency(item.total, trip.currency)}
                    </p>
                  </motion.div>
                ))}

                {/* Buffer */}
                <motion.div 
                  className="flex items-center justify-between p-4 rounded-2xl relative overflow-hidden"
                  style={{ 
                    backgroundColor: 'var(--swoopy-pink-soft)',
                    boxShadow: 'var(--shadow-clay-sm)'
                  }}
                  variants={itemVariants}
                  initial="hidden"
                  animate="visible"
                  transition={{ delay: 0.2 }}
                >
                  <div 
                    className="absolute top-0 left-0 right-0 h-1/2 pointer-events-none"
                    style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.4) 0%, rgba(255,255,255,0) 100%)' }}
                  />
                  <div className="relative z-10">
                    <p className="font-bold" style={{ color: 'var(--text-primary)' }}>Buffer</p>
                    <p className="text-xs font-semibold" style={{ color: 'var(--text-muted)' }}>{trip.details.budgetBreakdown.buffer.percentage}% for surprises</p>
                  </div>
                  <p className="font-extrabold text-lg relative z-10" style={{ color: 'var(--swoopy-pink)' }}>
                    {formatCurrency(trip.details.budgetBreakdown.buffer.total, trip.currency)}
                  </p>
                </motion.div>

                {/* Total */}
                <motion.div 
                  className="flex items-center justify-between p-5 rounded-2xl relative overflow-hidden"
                  style={{ 
                    background: 'linear-gradient(145deg, var(--swoopy-pink-light), var(--swoopy-pink), var(--swoopy-pink-dark))', 
                    color: 'white',
                    boxShadow: 'var(--shadow-clay-btn)'
                  }}
                  variants={itemVariants}
                  initial="hidden"
                  animate="visible"
                  transition={{ delay: 0.25 }}
                >
                  <div 
                    className="absolute top-0 left-0 right-0 h-1/2 pointer-events-none"
                    style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0) 100%)' }}
                  />
                  <p className="font-bold relative z-10">Grand Total</p>
                  <p className="text-2xl font-extrabold relative z-10">
                    {formatCurrency(trip.details.budgetBreakdown.grandTotal, trip.currency)}
                  </p>
                </motion.div>
              </div>
            </motion.div>
          )}

          {/* Stay Tab */}
          {activeTab === 'stay' && (
            <motion.div 
              key="stay"
              className="swoopy-card p-5"
              variants={tabVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
            >
              <h2 className="flex items-center gap-2 mb-5" style={{ 
                fontSize: '19px', 
                fontWeight: '800', 
                color: 'var(--text-primary)',
                fontFamily: "'Nunito', sans-serif"
              }}>
                <Home size={22} style={{ color: 'var(--swoopy-pink)' }} />
                Where to Stay
              </h2>

              {trip.details.whereToStay.neighborhoods.map((n, index) => (
                <motion.div 
                  key={index} 
                  className="mb-5 p-4 rounded-2xl relative overflow-hidden"
                  style={{ 
                    backgroundColor: 'var(--clay-white)',
                    boxShadow: 'var(--shadow-clay-sm)'
                  }}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <div 
                    className="absolute top-0 left-0 right-0 h-1/2 pointer-events-none"
                    style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.6) 0%, rgba(255,255,255,0) 100%)' }}
                  />
                  <div className="relative z-10">
                    <h3 className="font-extrabold mb-1" style={{ color: 'var(--text-primary)' }}>{n.name}</h3>
                    <p className="text-sm font-semibold mb-3" style={{ color: 'var(--text-muted)' }}>Best for: {n.bestFor}</p>
                    
                    <div className="flex flex-wrap gap-2 mb-4">
                      {n.accommodationTypes.map((type, i) => (
                        <span 
                          key={i} 
                          className="text-xs px-3 py-1.5 rounded-full font-bold"
                          style={{ 
                            backgroundColor: 'var(--card-bg)', 
                            color: 'var(--text-secondary)',
                            boxShadow: 'var(--shadow-clay-xs)'
                          }}
                        >
                          {type}
                        </span>
                      ))}
                    </div>

                    <p className="text-sm font-bold mb-2" style={{ color: 'var(--text-primary)' }}>Search these:</p>
                    <div className="space-y-2">
                      {n.searchPhrases.map((phrase, i) => (
                        <div 
                          key={i} 
                          className="flex items-center gap-2 p-3 rounded-xl"
                          style={{ 
                            backgroundColor: 'var(--card-bg)',
                            boxShadow: 'var(--shadow-clay-xs)'
                          }}
                        >
                          <code className="text-xs flex-1 font-bold" style={{ color: 'var(--text-secondary)' }}>{phrase}</code>
                          <motion.button
                            onClick={() => copyToClipboard(phrase, `stay-${index}-${i}`)}
                            className="p-2 rounded-lg transition-colors"
                            style={{ 
                              color: copiedText === `stay-${index}-${i}` ? 'var(--swoopy-pink)' : 'var(--text-muted)',
                              backgroundColor: copiedText === `stay-${index}-${i}` ? 'var(--swoopy-pink-soft)' : 'transparent'
                            }}
                            whileTap={{ scale: 0.9 }}
                          >
                            {copiedText === `stay-${index}-${i}` ? <Check size={16} /> : <Copy size={16} />}
                          </motion.button>
                        </div>
                      ))}
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          )}

          {/* Eat Tab */}
          {activeTab === 'eat' && (
            <motion.div 
              key="eat"
              className="swoopy-card p-5"
              variants={tabVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
            >
              <h2 className="flex items-center gap-2 mb-5" style={{ 
                fontSize: '19px', 
                fontWeight: '800', 
                color: 'var(--text-primary)',
                fontFamily: "'Nunito', sans-serif"
              }}>
                <Utensils size={22} style={{ color: 'var(--swoopy-pink)' }} />
                Where to Eat
              </h2>

              <div className="space-y-5">
                <motion.div
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.05 }}
                >
                  <h3 className="font-bold mb-3" style={{ color: 'var(--text-primary)' }}>Must-try dishes:</h3>
                  <div className="flex flex-wrap gap-2">
                    {trip.details.whereToEat.localDishes.map((dish, i) => (
                      <span 
                        key={i} 
                        className="px-4 py-2 rounded-full text-xs font-bold relative overflow-hidden"
                        style={{ 
                          backgroundColor: 'var(--swoopy-pink-soft)', 
                          color: 'var(--swoopy-pink)',
                          boxShadow: 'var(--shadow-clay-xs)'
                        }}
                      >
                        <div 
                          className="absolute top-0 left-0 right-0 h-1/2 pointer-events-none"
                          style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.4) 0%, rgba(255,255,255,0) 100%)' }}
                        />
                        <span className="relative z-10">{dish}</span>
                      </span>
                    ))}
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                >
                  <h3 className="font-bold mb-3" style={{ color: 'var(--text-primary)' }}>Food hotspots:</h3>
                  <div className="flex flex-wrap gap-2">
                    {trip.details.whereToEat.foodAreas.map((area, i) => (
                      <span 
                        key={i} 
                        className="text-xs px-4 py-2 rounded-full font-bold"
                        style={{ 
                          backgroundColor: 'var(--clay-white)', 
                          color: 'var(--text-secondary)',
                          boxShadow: 'var(--shadow-clay-xs)'
                        }}
                      >
                        {area}
                      </span>
                    ))}
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.15 }}
                >
                  <h3 className="font-bold mb-3" style={{ color: 'var(--text-primary)' }}>Search these:</h3>
                  <div className="space-y-2">
                    {trip.details.whereToEat.searchPhrases.map((phrase, i) => (
                      <div 
                        key={i} 
                        className="flex items-center gap-2 p-3 rounded-xl"
                        style={{ 
                          backgroundColor: 'var(--clay-white)',
                          boxShadow: 'var(--shadow-clay-xs)'
                        }}
                      >
                        <code className="text-sm flex-1 font-bold" style={{ color: 'var(--text-secondary)' }}>{phrase}</code>
                        <motion.button
                          onClick={() => copyToClipboard(phrase, `eat-${i}`)}
                          className="p-2 rounded-lg transition-colors"
                          style={{ 
                            color: copiedText === `eat-${i}` ? 'var(--swoopy-pink)' : 'var(--text-muted)',
                            backgroundColor: copiedText === `eat-${i}` ? 'var(--swoopy-pink-soft)' : 'transparent'
                          }}
                          whileTap={{ scale: 0.9 }}
                        >
                          {copiedText === `eat-${i}` ? <Check size={16} /> : <Copy size={16} />}
                        </motion.button>
                      </div>
                    ))}
                  </div>
                </motion.div>
              </div>
            </motion.div>
          )}

          {/* Plan Tab */}
          {activeTab === 'plan' && (
            <motion.div 
              key="plan"
              className="swoopy-card p-5"
              variants={tabVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
            >
              <h2 className="flex items-center gap-2 mb-5" style={{ 
                fontSize: '19px', 
                fontWeight: '800', 
                color: 'var(--text-primary)',
                fontFamily: "'Nunito', sans-serif"
              }}>
                <Calendar size={22} style={{ color: 'var(--swoopy-pink)' }} />
                Day-by-Day Plan
                {aiItinerary?.generated_by === 'ai' && (
                  <span 
                    className="text-xs px-3 py-1 rounded-full ml-2 font-extrabold"
                    style={{ backgroundColor: 'var(--video-teal-soft)', color: 'var(--video-teal)' }}
                  >
                    AI Generated
                  </span>
                )}
              </h2>

              {/* Loading State */}
              {itineraryLoading && (
                <div className="flex flex-col items-center justify-center py-10">
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                  >
                    <Loader2 size={32} style={{ color: 'var(--swoopy-pink)' }} />
                  </motion.div>
                  <p className="text-sm mt-4 font-bold" style={{ color: 'var(--text-muted)' }}>
                    Generating your personalized itinerary...
                  </p>
                </div>
              )}

              {/* Error State */}
              {itineraryError && (
                <motion.div 
                  className="p-4 mb-4 rounded-xl"
                  style={{ backgroundColor: 'var(--swoopy-pink-soft)' }}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                >
                  <p className="text-sm font-bold" style={{ color: 'var(--text-secondary)' }}>{itineraryError}</p>
                </motion.div>
              )}

              {/* AI Itinerary Content */}
              {aiItinerary && !itineraryLoading && (
                <motion.div 
                  className="mb-6 p-5 rounded-2xl prose prose-sm max-w-none relative overflow-hidden"
                  style={{ 
                    backgroundColor: 'var(--clay-white)',
                    boxShadow: 'var(--shadow-clay-sm)'
                  }}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                >
                  <div 
                    className="absolute top-0 left-0 right-0 h-1/3 pointer-events-none"
                    style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.5) 0%, rgba(255,255,255,0) 100%)' }}
                  />
                  <div className="relative z-10">
                    <ReactMarkdown
                      components={{
                        h1: ({children}) => <h1 style={{fontSize: '18px', fontWeight: '800', color: 'var(--text-primary)', marginBottom: '12px', fontFamily: "'Nunito', sans-serif"}}>{children}</h1>,
                        h2: ({children}) => <h2 style={{fontSize: '16px', fontWeight: '700', color: 'var(--swoopy-pink)', marginTop: '16px', marginBottom: '8px', fontFamily: "'Nunito', sans-serif"}}>{children}</h2>,
                        h3: ({children}) => <h3 style={{fontSize: '14px', fontWeight: '700', color: 'var(--text-primary)', marginTop: '12px', marginBottom: '4px'}}>{children}</h3>,
                        p: ({children}) => <p style={{fontSize: '14px', color: 'var(--text-secondary)', marginBottom: '8px', lineHeight: '1.7'}}>{children}</p>,
                        strong: ({children}) => <strong style={{color: 'var(--text-primary)', fontWeight: '700'}}>{children}</strong>,
                        li: ({children}) => <li style={{fontSize: '14px', color: 'var(--text-secondary)', marginLeft: '16px', marginBottom: '4px'}}>{children}</li>,
                        ul: ({children}) => <ul style={{marginBottom: '8px'}}>{children}</ul>,
                      }}
                    >
                      {aiItinerary.itinerary}
                    </ReactMarkdown>
                    {aiItinerary.generated_by === 'ai' && (
                      <p 
                        className="text-xs mt-4 pt-4 font-bold"
                        style={{color: 'var(--text-muted)', borderTop: '2px solid var(--border-soft)'}}
                      >
                        ✨ AI-generated itinerary personalized for your trip
                      </p>
                    )}
                  </div>
                </motion.div>
              )}

              {/* Fallback Mini Itinerary */}
              {!aiItinerary && !itineraryLoading && (
                <div className="space-y-3 mb-6">
                  {trip.details.miniItinerary.map((day, i) => (
                    <motion.div 
                      key={i} 
                      className="flex gap-3 p-4 rounded-2xl relative overflow-hidden"
                      style={{ 
                        backgroundColor: 'var(--clay-white)',
                        boxShadow: 'var(--shadow-clay-sm)'
                      }}
                      initial={{ opacity: 0, x: -12 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.05, type: 'spring' }}
                    >
                      <div 
                        className="absolute top-0 left-0 right-0 h-1/2 pointer-events-none"
                        style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.5) 0%, rgba(255,255,255,0) 100%)' }}
                      />
                      <div 
                        className="w-12 h-12 rounded-xl flex items-center justify-center font-extrabold text-sm flex-shrink-0 relative z-10"
                        style={{ 
                          background: 'linear-gradient(145deg, var(--swoopy-pink-light), var(--swoopy-pink))', 
                          color: 'white',
                          boxShadow: 'var(--shadow-clay-btn)'
                        }}
                      >
                        {day.day}
                      </div>
                      <div className="relative z-10">
                        <p className="font-extrabold mb-1" style={{ color: 'var(--text-primary)' }}>Day {day.day}</p>
                        <p className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>{day.plan}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}

              {/* Pro Tips */}
              <motion.div 
                className="p-5 rounded-2xl relative overflow-hidden"
                style={{ 
                  backgroundColor: 'var(--swoopy-pink-soft)',
                  boxShadow: 'var(--shadow-clay-sm)'
                }}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                <div 
                  className="absolute top-0 left-0 right-0 h-1/2 pointer-events-none"
                  style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.3) 0%, rgba(255,255,255,0) 100%)' }}
                />
                <div className="relative z-10">
                  <h3 className="font-extrabold mb-3 flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
                    <Lightbulb size={18} style={{ color: 'var(--swoopy-pink)' }} />
                    Pro Tips
                  </h3>
                  <ul className="space-y-2">
                    {trip.details.smartTips.map((tip, i) => (
                      <li key={i} className="text-sm flex items-start gap-2 font-medium" style={{ color: 'var(--text-secondary)' }}>
                        <span style={{ color: 'var(--swoopy-pink)' }}>•</span>
                        {tip}
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Disclaimer */}
        <motion.p 
          className="text-center text-xs mt-6 font-bold"
          style={{ color: 'var(--text-muted)' }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
        >
          Estimates are approximate — your adventure may vary! 🌟
        </motion.p>

        {/* Prepare for Your Trip Section - Premium Features */}
        <motion.section 
          className="mt-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <h2 
            className="text-sm font-bold mb-4 tracking-wider"
            style={{ color: 'var(--text-muted)' }}
          >
            PREPARE FOR YOUR TRIP
          </h2>
          
          <div className="space-y-3">
            {/* Offline Maps Card */}
            <motion.button
              onClick={() => {
                const isPremium = userStatus?.planType === 'basic' || userStatus?.planType === 'pro';
                if (isPremium) {
                  setCurrentView('offline-maps');
                } else {
                  toast.info('Upgrade to access Offline Maps', {
                    description: 'Save your trip data for offline access',
                    action: {
                      label: 'Upgrade',
                      onClick: () => window.location.href = '/pricing'
                    }
                  });
                }
              }}
              className="w-full p-4 rounded-2xl flex items-center gap-4 text-left relative overflow-hidden"
              style={{ 
                backgroundColor: 'var(--card-bg)',
                boxShadow: 'var(--shadow-clay-md)'
              }}
              whileHover={{ y: -2, boxShadow: 'var(--shadow-clay-lg)' }}
              whileTap={{ scale: 0.99 }}
              data-testid="offline-maps-card"
            >
              <div 
                className="absolute top-0 left-0 right-0 h-1/2 pointer-events-none"
                style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.5) 0%, rgba(255,255,255,0) 100%)' }}
              />
              <div 
                className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 relative z-10"
                style={{ 
                  background: 'linear-gradient(145deg, #4ECDC4, #44A08D)',
                  boxShadow: '0 4px 12px rgba(78, 205, 196, 0.3)'
                }}
              >
                <Map size={22} color="white" />
              </div>
              <div className="flex-1 relative z-10">
                <div className="flex items-center gap-2">
                  <h3 className="font-bold" style={{ color: 'var(--text-primary)' }}>
                    Offline Maps
                  </h3>
                  {(!userStatus?.planType || userStatus?.planType === 'free') && (
                    <span 
                      className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-bold"
                      style={{ backgroundColor: 'var(--swoopy-pink-soft)', color: 'var(--swoopy-pink)' }}
                    >
                      <Crown size={10} />
                      Premium
                    </span>
                  )}
                </div>
                <p className="text-xs font-medium mt-0.5" style={{ color: 'var(--text-muted)' }}>
                  Save trip data for offline access
                </p>
              </div>
              <ChevronRight size={20} style={{ color: 'var(--text-muted)' }} className="relative z-10" />
            </motion.button>

            {/* Smart Packing Card */}
            <motion.button
              onClick={() => {
                const isPremium = userStatus?.planType === 'basic' || userStatus?.planType === 'pro';
                if (isPremium) {
                  setCurrentView('smart-packing');
                } else {
                  toast.info('Upgrade to access Smart Packing', {
                    description: 'Get AI-suggested packing lists for your trip',
                    action: {
                      label: 'Upgrade',
                      onClick: () => window.location.href = '/pricing'
                    }
                  });
                }
              }}
              className="w-full p-4 rounded-2xl flex items-center gap-4 text-left relative overflow-hidden"
              style={{ 
                backgroundColor: 'var(--card-bg)',
                boxShadow: 'var(--shadow-clay-md)'
              }}
              whileHover={{ y: -2, boxShadow: 'var(--shadow-clay-lg)' }}
              whileTap={{ scale: 0.99 }}
              data-testid="smart-packing-card"
            >
              <div 
                className="absolute top-0 left-0 right-0 h-1/2 pointer-events-none"
                style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.5) 0%, rgba(255,255,255,0) 100%)' }}
              />
              <div 
                className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 relative z-10"
                style={{ 
                  background: 'linear-gradient(145deg, #F5A3B5, var(--swoopy-pink))',
                  boxShadow: '0 4px 12px rgba(233, 116, 143, 0.3)'
                }}
              >
                <Briefcase size={22} color="white" />
              </div>
              <div className="flex-1 relative z-10">
                <div className="flex items-center gap-2">
                  <h3 className="font-bold" style={{ color: 'var(--text-primary)' }}>
                    Smart Packing
                  </h3>
                  {(!userStatus?.planType || userStatus?.planType === 'free') && (
                    <span 
                      className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-bold"
                      style={{ backgroundColor: 'var(--swoopy-pink-soft)', color: 'var(--swoopy-pink)' }}
                    >
                      <Crown size={10} />
                      Premium
                    </span>
                  )}
                </div>
                <p className="text-xs font-medium mt-0.5" style={{ color: 'var(--text-muted)' }}>
                  AI-powered packing checklist
                </p>
              </div>
              <ChevronRight size={20} style={{ color: 'var(--text-muted)' }} className="relative z-10" />
            </motion.button>
          </div>
        </motion.section>
      </div>
    </div>
  );
}
